import { createPortal } from 'react-dom';

/**
 * Get from a list of objects the item from index
 * @param {object} objectList
 * @param {number} itemIndex
 * */
export const getObjectItemByIndex = (objectList, itemIndex) => {
  const itemKey = Object.keys(objectList).find(
    (key, index) => index === itemIndex,
  );

  return itemKey ? objectList[itemKey] : null;
};

/**
 * Given a list of allwed Calculator Items check
 * if an itemId is allowed calculator to be use
 * @param {allowedItems} calculatorAllowedItems
 * @params {itemId} itemId
 */
export const isCalculatorAllowed = (allowedItems, itemId) => {
  if (!allowedItems) {
    return false;
  }
  const predicate = (element) => element.learnosityItemReference === itemId;
  return allowedItems.some(predicate);
};

/**
 * Format a given time in seconds to MM:SS
 * @param {time: Number}
 **/
export const formatTime = (time, a11y = false) => {
  const timeInt = parseInt(time, 10);
  if (Number.isNaN(timeInt)) {
    return `00:00`;
  }
  // because the time can overflow because extend time and milliseconds, etc.
  // a fair bit of logic is necessary to format this string in a way that doesn't
  // inject NaN values.
  let minutes = Math.floor(timeInt / 60);
  if (Number.isNaN(minutes) || minutes < 0) {
    minutes = 0;
  }
  let seconds = timeInt - minutes * 60;
  if (Number.isNaN(seconds) || seconds < 0) {
    seconds = 0;
  }

  let minutesString = `0${minutes}`;
  const secondsString = `0${seconds}`;
  const minutesLength = minutesString.length;
  minutesString =
    minutesLength >= 4
      ? minutesString.substr(1 - minutesLength)
      : minutesString.substr(-2);
  return a11y
    ? `${minutesString} minutes ${secondsString.substr(-2)} seconds`
    : `${minutesString}:${secondsString.substr(-2)}`;
};

/**
 * Check if the question has custom distractor rationale
 * @param {Object} questionMetadata - The question metadata object from learnosity
 * @returns {boolean} - Whether the question has custom distractor rationale
 */
export const hasCustomDistractorRationale = (questionMetadata) =>
  !!questionMetadata?.custom_distractor_rationale_response_level;

/**
 * Extract the props for the CustomDistractorRationale component from the metadata and response
 * @param {Object} activeQuestion - The active question object from learnosity
 * @param {*} questionResponse - The question response object from learnosity
 * @param {*} questionMetadata - The question metadata object from learnosity
 * @returns {Object} - The props for the CustomDistractorRationale component
 */
export const getCustomDistractorRationaleProps = (
  activeQuestion,
  questionResponse,
  questionMetadata,
) => {
  if (!activeQuestion || !questionResponse) {
    return {
      isCorrectOption: [],
      optionNumber: [],
      distractorContent: [],
    };
  }

  const learnosityCorrectOptions =
    activeQuestion.getQuestion()?.validation?.valid_response?.value;

  const { value: selectedOptions } = questionResponse;
  const { custom_distractor_rationale_response_level: customDistractors } =
    questionMetadata;

  // Learnosity might be returning the selected options with some letters, eg: 'i1'
  const optionsValue =
    activeQuestion.getProcessedOptions()?.map((option) => option.value) || [];
  selectedOptions.forEach((option, index) => {
    selectedOptions[index] = String(optionsValue.indexOf(option));
  });

  // Learnosity might be returning the correct options with some letters, eg: 'i1'
  const correctOptions = learnosityCorrectOptions?.map((option) =>
    String(optionsValue.indexOf(option)),
  );

  return {
    correctOptions,
    selectedOptions,
    customDistractors,
  };
};

/**
 * Injects a custom distractor rationale into the learnosity distractor wrapper
 * @param {React.component} children - The children to render in the learnosity distractor wrapper
 * @returns {React.component} - The children wrapped in a portal to the learnosity distractor wrapper
 */
export const injectCustomDistractorRationale = (
  children,
  questionReference,
) => {
  const learnosityDistractorWrapperSelector = `div[data-reference="${questionReference}"] div.lrn_distractor_rationale_wrapper`;

  if (!document.querySelector(learnosityDistractorWrapperSelector)) {
    return null;
  }
  const learnosityDistractorWrapperElement = document.querySelector(
    learnosityDistractorWrapperSelector,
  );

  return createPortal(children, learnosityDistractorWrapperElement);
};

export const isCustomDistractorRationaleSaved = (
  questionReference,
  customDistractorsProps,
) => !!customDistractorsProps[questionReference];

/**
 * Convert the question number to a letter
 * @param number - The number to convert to a letter
 * @returns - The letter corresponding to the number
 */
export const convertQuestionNumberToLetter = (number) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return alphabet.charAt(Number(number));
};

/**
 * Convert the learnosity generated distractor rationale number to a letter
 */
export const convertLearnosityDistractorRationaleNumberToLetter = (
  itemReference,
  selectedOptions,
) => {
  const questionElement = document.querySelector(
    `div[data-reference="${itemReference}"]`,
  );
  const DISTRACTOR_NUMBER_CONTAINER_SELECTOR =
    'div.lrn_validation_stem_numeration';
  const distractorNumberContainers = questionElement.querySelectorAll(
    DISTRACTOR_NUMBER_CONTAINER_SELECTOR,
  );
  distractorNumberContainers.forEach((container, index) => {
    const numberContainer = container;
    numberContainer.innerText = convertQuestionNumberToLetter(
      selectedOptions[index],
    );
  });
};
